/**
 * @file useWriteoffCommit.ts
 * @description Проведение списания с подтверждением резервов (phase19.13 этап 0).
 *
 * Сервер отвечает 409 `writeoff_reserved_stock`, когда списание уводит остаток
 * ниже активных резервов заказов. Хук держит распознанные конфликты: пока они
 * непустые — страница показывает диалог подтверждения, а повтор идёт с
 * `confirmReservedWriteoff: true`.
 *
 * После успешного проведения хук зовёт `onCommitted(writeoffId)` — карточку
 * перечитывают с сервера: ответ commit'а приходит без позиций и автора
 * (phase19.23 этап A, дефект «устаревшая шапка после проведения»).
 */

import { useState } from "react";

import { useWmsConfirm, type WmsConfirmRequest } from "./use-wms-confirm";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  WRITEOFF_RESERVED_STOCK_CODE,
  parseReservedConflicts,
  writeoffsApi,
  type WriteoffReservedConflict,
} from "../../lib/api-wms";

interface UseWriteoffCommitResult {
  /** `confirmed = true` — повтор после подтверждения кладовщиком. */
  commit: (confirmed: boolean) => Promise<void>;
  committing: boolean;
  /** Непустой массив = показать диалог подтверждения. */
  conflicts: WriteoffReservedConflict[];
  dismissConflicts: () => void;
  /** Вопрос «Провести списание?» — рисует страница (`WmsConfirmDialog`). */
  confirmRequest: WmsConfirmRequest | null;
  settleConfirm: (ok: boolean) => void;
}

export function useWriteoffCommit(
  writeoffId: string | undefined,
  /**
   * Перечитать документ после проведения. Ответ `commit` — «голый» Writeoff
   * без `items` и `createdBy`, поэтому карточку обновляем перезагрузкой.
   */
  onCommitted: (writeoffId: string) => Promise<void> | void,
): UseWriteoffCommitResult {
  const notify = useNotify();
  const [committing, setCommitting] = useState(false);
  const [conflicts, setConflicts] = useState<WriteoffReservedConflict[]>([]);
  const asker = useWmsConfirm();

  async function commit(confirmed: boolean): Promise<void> {
    if (!writeoffId) return;
    // Окно кабинета вместо системного `confirm()` (phase19.31).
    if (
      !confirmed &&
      !(await asker.confirm({ title: "Провести списание?", description: "Остаток на складе уменьшится.", action: "Провести" }))
    ) {
      return;
    }
    setCommitting(true);
    try {
      await writeoffsApi.commit(writeoffId, {
        confirmReservedWriteoff: confirmed,
      });
      notify.success("Списание проведено");
      setConflicts([]);
      try {
        await onCommitted(writeoffId);
      } catch {
        // Документ проведён, не обновился только экран — говорим об этом прямо,
        // иначе кладовщик подумает, что списание не прошло, и нажмёт ещё раз.
        notify.error("Списание проведено, но карточка не обновилась — обновите страницу");
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === WRITEOFF_RESERVED_STOCK_CODE) {
        const parsed = parseReservedConflicts(err.details);
        if (parsed.length > 0) {
          setConflicts(parsed);
          return;
        }
      }
      notify.error(err instanceof ApiError ? err.message : "Не удалось");
    } finally {
      setCommitting(false);
    }
  }

  return {
    commit,
    committing,
    conflicts,
    dismissConflicts: () => setConflicts([]),
    confirmRequest: asker.request,
    settleConfirm: asker.settle,
  };
}
